/**
 * Performance, Portfolio and Distribution tabs: the leaderboard fields they read beyond
 * what the scorer serves (src/financial/portfolio-board.ts), and the masked resident
 * list behind an expanded heatmap row (src/aithora/v3/finance/performance-board.ts).
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { BenchMetricKey, Leaderboard, LeaderboardEntry } from './performance-types';

/** A leaderboard entry with the fields the heatmap draws. */
export type PortfolioEntry = LeaderboardEntry & {
  /** Composite change since the previous recorded quarter, in points; null without two quarters. */
  delta?: number | null;
  /** MODELED annual revenue worth one grade step: a 4% census lift priced at the building's CMS cost-report revenue per resident; null in the top band or without the inputs. */
  upsideUsd?: number | null;
  /** Measured uncaptured reimbursement per year (collectible leakage this month x 12), in dollars. */
  leakageUsdYear?: number | null;
};

/** The leaderboard as Portfolio and Distribution read it. */
export type PortfolioBoard = Omit<Leaderboard, 'facilities'> & {
  facilities: PortfolioEntry[];
  /** Which metrics come from Aithora's own care tables (live) rather than CMS. All sixteen are 'cms' today. */
  metricSources?: Partial<Record<BenchMetricKey, 'live' | 'cms'>>;
  upsideBasis?: 'modeled';
  upsideNote?: string;
};

/** One resident needing action in a building, masked (code, initials, room only). */
export interface MaskedResident {
  /** "#A1B2": a salted, stable code, never the resident id. */
  code: string;
  initials: string;
  /** '' when no room is on the profile. */
  room: string;
  /** Wellbeing BAND from the mood record: low, fair, good, high or unknown (never the raw score). */
  mood: string;
  billing: 'ready' | 'blocked';
}
export interface ResidentsNeedingAction {
  facilityId: string;
  residents: MaskedResident[];
  /** Residents on the roster (the list holds only those needing action). */
  total?: number;
  masked?: true;
  real: boolean;
}

/** GET /performance/facilities/:id/residents-needing-action: minimum necessary, masked. Aithora staff, or the building's own admin; the partner is refused. */
export const residentsNeedingAction = route<ResidentsNeedingAction>()('GET', `${AITHORA_PREFIX}/performance/facilities/:id/residents-needing-action`, { honest: true });

export const performancePortfolioApi = { residentsNeedingAction } as const;
